import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Phone, Menu, X, Calendar, Sun, Moon } from 'lucide-react';
import { doctorInfo } from '../mockData';
import { useTheme } from '../context/ThemeContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const handleBookConsultation = () => {
    if (window.location.pathname !== '/') {
      window.location.href = '/#book-appointment';
    } else {
      document.getElementById('book-appointment')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-blue-100 dark:bg-slate-900 shadow-md dark:shadow-slate-800/50'
          : 'bg-blue-100/95 dark:bg-slate-900/95 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo/Name */}
          <div className="cursor-pointer" onClick={() => scrollToSection('top')}>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-slate-50">
              {doctorInfo.name}
            </h1>
            <div className="text-[10px] md:text-xs text-gray-600 dark:text-slate-400 max-w-md">
              <div>MBBS, MD Medicine</div>
              <div>DM Pulmonary, Critical Care and Sleep Medicine</div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-5">
            <button 
              onClick={() => scrollToSection('top')}
              className="text-gray-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors font-medium text-sm"
            >
              Home
            </button>
            <button 
              onClick={() => scrollToSection('services')}
              className="text-gray-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors font-medium text-sm"
            >
              Services
            </button>
            <Link to="/conditions" className="text-gray-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors font-medium text-sm">
              Conditions
            </Link>
            <Link to="/blog" className="text-gray-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors font-medium text-sm">
              Blog
            </Link>
            <button 
              onClick={() => scrollToSection('contact')}
              className="text-gray-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors font-medium text-sm"
            >
              Contact
            </button>
            
            <div className="w-px h-6 bg-gray-300 dark:bg-slate-600"></div>

            {/* Dark Mode Toggle */}
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle-btn"
              className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-300 dark:border-slate-600 text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            
            <a href={`tel:${doctorInfo.phone}`}>
              <Button variant="outline" size="sm" className="border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800">
                <Phone className="w-4 h-4 mr-1" />
                Call Now
              </Button>
            </a>

            <Button 
              size="sm" 
              className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400 text-white"
              onClick={handleBookConsultation}
              data-testid="header-book-btn"
            >
              <Calendar className="w-4 h-4 mr-1" />
              Book Appointment
            </Button>
          </nav>

          {/* Mobile: Theme Toggle + Menu Button */}
          <div className="lg:hidden flex items-center gap-2">
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle-mobile-btn"
              className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-300 dark:border-slate-600 text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <button 
              className="text-gray-700 dark:text-slate-300"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <nav className="lg:hidden mt-4 pt-4 border-t border-gray-200 dark:border-slate-700">
            <div className="flex flex-col gap-4">
              <button 
                onClick={() => scrollToSection('top')}
                className="text-left text-gray-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors font-medium py-2"
              >
                Home
              </button>
              <button 
                onClick={() => scrollToSection('services')}
                className="text-left text-gray-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors font-medium py-2"
              >
                Services
              </button>
              <Link 
                to="/conditions" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-left text-gray-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors font-medium py-2"
              >
                Conditions
              </Link>
              <Link 
                to="/blog" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-left text-gray-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors font-medium py-2"
              >
                Blog
              </Link>
              <button 
                onClick={() => scrollToSection('contact')}
                className="text-left text-gray-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors font-medium py-2"
              >
                Contact
              </button>
              
              <a href={`tel:${doctorInfo.phone}`}>
                <Button variant="outline" className="w-full border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300">
                  <Phone className="w-4 h-4 mr-2" />
                  Call Now
                </Button>
              </a>
              
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400 text-white"
                onClick={handleBookConsultation}
              >
                <Calendar className="w-4 h-4 mr-2" />
                Book Appointment
              </Button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
